import { Injectable, OnDestroy, inject } from '@angular/core';
import { SESSION_TIMEOUT, SYSTEM_CONST } from '../../constants/system-constants.model';
import { IKeycloakTokenResponse } from '../../models/keycloak-token-detail.model';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { Auth } from '../../store/auth/auth.action';
import { Store } from '@ngxs/store';
import { CommonService, TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { LOCAL_STORAGE } from '../../constants/localstorage-enum';
import { AuthState } from '../../store/auth/auth.state';
import { FormTrackerService } from '../common/form-tracker.service';
import { ToastrHelperService } from '../common/toast-helper.service';

@Injectable({
    providedIn: 'root'
})
export class TokenRefreshService implements OnDestroy {
    private store = inject(Store);
    private translation = inject(TranslationService);
    private commonService = inject(CommonService);
    private formTrackerService = inject(FormTrackerService);
    private toastrHelperService = inject(ToastrHelperService);

    private timerExpiringTimeout = null;
    private timerExpiredTimeout = null;
    dialogRef: MatDialogRef<any> = null;
    sessionExpiring$ = new BehaviorSubject<boolean>(false);
    sessionExpired$ = new BehaviorSubject<boolean>(false);
    private destroy$ = new Subject<void>();

    startTimer() {
        const authDetails = JSON.parse(localStorage.getItem(LOCAL_STORAGE.AUTH_DETAILS) as string) as IKeycloakTokenResponse;
        if (authDetails) {
            if (new Date().getTime() >= authDetails.refreshExpirationTime)
                this.dialogRef?.close();
            else {
                const dialogStartTime = authDetails.refreshExpirationTime - (SESSION_TIMEOUT.DIALOG_BEFORE_TIME * 60 * 1000);
                const expiredTime = authDetails.refreshExpirationTime - new Date().getTime();
                const expiringTime = dialogStartTime - new Date().getTime();
                this.timerExpiredTimeout = setTimeout(this.timerExpired.bind(this), Math.max(expiredTime, 0));
                this.timerExpiringTimeout = setTimeout(this.timerExpiring.bind(this), Math.max(expiringTime, 0));
            }
        }
    }

    stopTimer() {
        if (this.timerExpiringTimeout)
            clearTimeout(this.timerExpiringTimeout);

        if (this.timerExpiredTimeout)
            clearTimeout(this.timerExpiredTimeout);
    }

    resetTimer() {
        this.stopTimer();
        this.startTimer();
    }

    timerExpiring() {
        if (this.timerExpiringTimeout) {
            // cancel the dialog timer, but leave timerExpiredTimeout running 
            clearTimeout(this.timerExpiringTimeout);
        }
        // this event will prompt a warning dialog to the user 
        this.sessionExpiring$.next(true);
    }

    timerExpired() {
        this.stopTimer();
        // this event logs the user out (session timer has expired)
        this.sessionExpired$.next(true);
    };

    setSessionTimeout(sessionTimeout: string, displayMessage: boolean = false) {
        if (sessionTimeout === SESSION_TIMEOUT.CONTINUE)
            this.sessionTimeoutContinue();
        else if (sessionTimeout === SESSION_TIMEOUT.LOGOUT) {
            this.sessionTimeoutLogout();
            if (displayMessage)
                this.toastrHelperService.showErrorMessage('SESSION.EXPIRED_MESSAGE');
        }
    }

    sessionTimeoutLogout() {
        this.dialogRef?.close();
        this.store.dispatch(new Auth.Logout(this.store.selectSnapshot(AuthState.isPatientUser), true));
    }

    sessionTimeoutContinue() {
        this.dialogRef?.close();
        this.resetTimer();
    }

    displaySessionTimeoutDialog() {
        this.dialogRef = this.commonService.confirmDialodMessage({
            displayOnTop: true,
            title: this.translation.instant(SYSTEM_CONST.CONFIRM),
            message: this.translation.instant('SESSION.EXPIRING_MESSAGE', { minute: SESSION_TIMEOUT.DIALOG_BEFORE_TIME }),
            primaryButtonCallback: () => this.store.dispatch(new Auth.RefreshToken()).pipe(takeUntil(this.destroy$)).subscribe(() => this.setSessionTimeout(SESSION_TIMEOUT.CONTINUE)),
            secondaryButtonCallback: () => {
                this.formTrackerService.triggerLogout();
                this.setSessionTimeout(SESSION_TIMEOUT.LOGOUT);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}